import React from "react";
import styled, { keyframes } from "styled-components";
import AuthenticatedLink from "./AuthenticatedLink";
import Button, { ActivateFeatureButton } from "./Button";
import LottieLoadingThreeDots from "./LottieLoadingThreeDots";
import ArrowDownSignToNavigate from "src/img/arrow-down-sign-to-navigate.svg";
import ArrowDownSignToNavigateLight from "src/img/arrow-down-sign-to-navigate-light.svg";
import SearchIcon from "src/img/search-icon.svg";
import checkIcon from "src/img/check@2x.png";

export const portalColors = {
    border: "var(--calendar-border, #D8DEEC)",
    borderStrong: "var(--calendar-border-strong, #C9D2E7)",
    inputBorder: "var(--calendar-input-border, #95a6c8)",
    inputShadow: "var(--calendar-input-shadow, 0px 3px 6px rgba(0, 0, 0, 0.12))",
    text: "var(--calendar-text, #171A22)",
    mutedText: "var(--calendar-text-muted, #6A7387)",
    textSoft: "var(--calendar-text-soft, #8A95AB)",
    blue: "var(--calendar-action-primary-bg, #5B6EF5)",
    blueDark: "var(--calendar-action-primary-bg, #5B6EF5)",
    blueSoft: "var(--calendar-accent-soft, rgba(91,110,245,0.12))",
    actionPrimary: "var(--calendar-action-primary-bg, #5B6EF5)",
    actionPrimaryText: "var(--calendar-action-primary-text, #FFFFFF)",
    darkControlText: "var(--calendar-dark-control-text, #FFFFFF)",
    darkInk: "var(--calendar-dark-ink, #171A22)",
    danger: "#D64545",
    surface: "var(--calendar-surface, #FFFFFF)",
    surfaceSoft: "var(--calendar-surface-secondary, #F3F5FB)",
    surfaceTertiary: "var(--calendar-surface-tertiary, #EEF2F9)",
    shadow: "var(--calendar-shadow, 0 18px 36px rgba(29, 42, 89, 0.08))"
};

export const portalFadeUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const reducedMotion = `
    @media (prefers-reduced-motion: reduce) {
        animation: none;
        transition: none;
    }
`;

export const PortalPageShell = styled.div`
    --portal-page-gutter: ${p => p.gutter || "1rem"};

    width: calc(100% - (var(--portal-page-gutter) * 2));
    max-width: min(${p => p.maxWidth || "98.625rem"}, calc(100% - (var(--portal-page-gutter) * 2)));
    height: ${p => p.height || "auto"};
    min-height: ${p => p.minHeight || "0"};
    overflow: ${p => p.overflow || "visible"};
    margin: ${p => p.margin || "0 var(--portal-page-gutter)"};
    display: flex;
    flex-direction: column;
    align-items: stretch;
    min-width: 0;
    box-sizing: border-box;
    flex-grow: 1;
    padding-bottom: ${p => p.paddingBottom || "2rem"};

    @media (max-width: 640px) {
        --portal-page-gutter: ${p => p.mobileGutter || p.gutter || "1rem"};

        margin: ${p => p.margin || "0 var(--portal-page-gutter)"};
        padding-bottom: ${p => p.paddingBottom || "1.5rem"};
    }
`;

export const PortalWorkspace = styled.section`
    width: 100%;
    max-width: ${p => p.maxWidth || "none"};
    display: grid;
    grid-template-columns: ${p => p.columns || "minmax(0, 1fr)"};
    column-gap: ${p => p.columnGap || p.gap || "1rem"};
    row-gap: ${p => p.rowGap || "0"};
    align-items: ${p => p.alignItems || "start"};
    box-sizing: border-box;

    @media (max-width: ${p => p.breakpoint || "1100px"}) {
        grid-template-columns: 1fr;
    }
`;

export const PortalSurface = styled.section`
    width: 100%;
    min-width: 0;
    background: ${p => p.background || portalColors.surface};
    border: 1px solid ${p => p.border || portalColors.border};
    border-radius: ${p => p.radius || "16px"};
    box-shadow: ${p => p.shadow || portalColors.shadow};
    padding: ${p => p.padding || "1rem"};
    margin-bottom: ${p => p.marginBottom || "1rem"};
    box-sizing: border-box;
    overflow: ${p => p.overflow || "hidden"};
    animation: ${portalFadeUp} 0.22s ease both;
    transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;

    ${p => p.$interactive && `
        &:hover {
            border-color: ${portalColors.borderStrong};
            box-shadow: 0 22px 44px rgba(29, 42, 89, 0.11);
            transform: translateY(-1px);
        }
    `}

    ${reducedMotion}
`;

export const PortalSurfaceStack = styled.div`
    width: 100%;
    max-width: ${p => p.maxWidth || "none"};
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

export const PortalPanelHeader = styled.div`
    display: flex;
    align-items: ${p => p.alignItems || "flex-start"};
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: ${p => p.withDivider ? "0.85rem" : "0"};
    margin-bottom: ${p => p.marginBottom || (p.withDivider ? "1rem" : "0")};
    border-bottom: ${p => p.withDivider ? `1px solid ${portalColors.border}` : "none"};

    @media (max-width: 720px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

export const PortalKicker = styled.div`
    color: ${p => p.color || portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    line-height: 1.2;
    text-transform: uppercase;
    margin-bottom: 0.35rem;
`;

export const PortalSectionTitle = styled.h2`
    color: ${p => p.color || portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "1rem"};
    line-height: 1.25;
    font-weight: 800;
    margin: 0 0 8px;
`;

export const PortalCaption = styled.p`
    color: ${p => p.color || portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "0.875rem"};
    line-height: 1.5;
    font-weight: 500;
    margin: ${p => p.margin || "0.35rem 0 0"};
    max-width: ${p => p.maxWidth || "44rem"};
`;

export const PortalActionBar = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: ${p => p.justifyContent || "space-between"};
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: ${p => p.padding || "0.85rem 1rem"};
    background: ${p => p.background || portalColors.surface};
    border: 1px solid ${p => p.border || portalColors.border};
    border-radius: ${p => p.radius || "16px"};
    box-shadow: ${p => p.shadow || portalColors.shadow};
    box-sizing: border-box;
    animation: ${portalFadeUp} 0.2s ease both;
    transition: border-color 0.16s ease, box-shadow 0.16s ease;

    @media (max-width: 760px) {
        align-items: stretch;
        flex-direction: column;
    }

    ${reducedMotion}
`;

export const PortalTableToolbar = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: nowrap;
    gap: 0.75rem;
    width: 100%;
    max-width: ${p => p.maxWidth || "98.625rem"};
    min-height: ${p => p.minHeight || "4.75rem"};
    padding: ${p => p.padding || "0.85rem 1rem"};
    background: ${p => p.background || portalColors.surface};
    border: 1px solid ${p => p.border || portalColors.border};
    border-bottom: ${p => p.withBottomBorder ? `1px solid ${portalColors.border}` : "none"};
    border-radius: ${p => p.radius || "16px 16px 0 0"};
    box-shadow: ${p => p.shadow || portalColors.shadow};
    box-sizing: border-box;
    animation: ${portalFadeUp} 0.2s ease both;
    transition: border-color 0.16s ease, box-shadow 0.16s ease;

    @media (max-width: 760px) {
        align-items: stretch;
        flex-direction: column;
    }

    ${reducedMotion}
`;

export const PortalTableControls = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: ${p => p.gap || "0.75rem"};
    min-width: 0;
    flex: 1 1 auto;

    @media (max-width: 760px) {
        align-items: stretch;
        width: 100%;

        > * {
            max-width: 100%;
        }

        input,
        select,
        button {
            width: 100%;
        }
    }
`;

export const PortalTableActions = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: ${p => p.gap || "0.75rem"};
    margin-left: auto;
    flex: 0 0 auto;

    @media (max-width: 760px) {
        justify-content: stretch;
        margin-left: 0;
        width: 100%;

        > *,
        button {
            width: 100%;
            max-width: 100%;
        }
    }
`;

export const PortalControlGroup = styled.div`
    display: flex;
    align-items: center;
    justify-content: ${p => p.justifyContent || "flex-start"};
    flex-wrap: wrap;
    gap: ${p => p.gap || "0.75rem"};
    min-width: 0;
    margin-left: ${p => p.justifyContent === "flex-end" ? "auto" : "0"};
    align-self: ${p => p.justifyContent === "flex-end" ? "flex-start" : "auto"};

    @media (max-width: 760px) {
        align-items: stretch;
        width: 100%;
        margin-left: 0;

        > * {
            max-width: 100%;
        }

        input,
        select,
        button {
            width: 100%;
        }
    }
`;

export const PortalIconActionButton = styled(ActivateFeatureButton)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${p => p.gap || "0.45rem"};
    width: ${p => p.width || "auto"};
    min-width: ${p => p.minWidth || "16rem"};
    height: ${p => p.height || "42px"};
    padding: ${p => p.padding || "0 1rem"};
    border-radius: ${p => p.borderRadius || "8px"};

    @media (max-width: 620px) {
        width: 100%;
        min-width: 0;
    }
`;

export const PortalExportLink = styled(AuthenticatedLink)`
    width: ${p => p.width || "auto"};
    min-width: ${p => p.minWidth || "0"};
    height: ${p => p.height || "42px"};
    padding: ${p => p.padding || "0 1.25rem"};
    border-radius: ${p => p.borderRadius || "8px"};
    border: ${p => p.border || "1px solid var(--calendar-dark-ink, #171A22)"};
    background: var(--calendar-dark-ink, #171A22);
    color: #ffffff;
    box-shadow: ${p => p.boxShadow || "0px 3px 6px #00000029"};
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.fontSize || "0.875rem"};
    font-weight: ${p => p.fontWeight || 700};
    line-height: 1;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: transform 0.16s ease, opacity 0.16s ease, background 0.16s ease, color 0.16s ease;

    [data-theme="dark"] & {
        background: #F3F6FB;
        border-color: #F3F6FB;
        color: #000000;
    }

    &:hover {
        opacity: 0.92;
        transform: translateY(-1px);
    }

    @media (max-width: 620px) {
        width: 100%;
        min-width: 0;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        &:hover {
            transform: none;
        }
    }
`;

export const PortalIconActionButtonIcon = styled.img`
    width: ${p => p.width || "23px"};
    height: ${p => p.height || "19px"};
    flex: 0 0 auto;
    margin: ${p => p.margin || "0"};
`;

export const PortalIconActionButtonGlyph = styled.span`
    width: ${p => p.width || "23px"};
    height: ${p => p.height || "19px"};
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${p => p.color || "currentColor"};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "1.25rem"};
    font-weight: ${p => p.weight || 800};
    line-height: 1;
    margin: ${p => p.margin || "0"};
`;

export const PortalSearchInput = styled.input`
    width: ${p => p.width || "min(100%, 22.5rem)"};
    min-width: ${p => p.minWidth || "14rem"};
    height: 42px;
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    box-shadow: none;
    box-sizing: border-box;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 0 1rem 0 2.65rem;
    text-align: left;
    background-image: url("${SearchIcon}");
    background-repeat: no-repeat;
    background-position: left 1rem center;
    background-size: 18px 18px;
`;

export const PortalSelect = styled.select`
    height: ${p => p.height || "42px"};
    width: ${p => p.width || "12.5rem"};
    min-width: ${p => p.minWidth || "10.5rem"};
    border-radius: 8px;
    background: ${p => p.background || portalColors.surfaceSoft};
    box-shadow: none;
    border: 1px solid ${portalColors.border};
    padding: 0 2.35rem 0 0.9rem;
    color: ${portalColors.text};
    font-size: 0.875rem;
    font-family: Manrope, Helvetica, sans-serif;
    font-weight: 700;
    background-image: url("${ArrowDownSignToNavigate}");
    background-repeat: no-repeat;
    background-position: right 0.85rem center;
    background-size: 14px 14px;
    appearance: none;

    [data-theme="dark"] & {
        background-image: url("${ArrowDownSignToNavigateLight}");
    }

    &:focus,
    &:focus-visible {
        outline: none;
        border-color: ${portalColors.text};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
    }

    &:disabled {
        background-color: ${portalColors.surfaceTertiary || "#EEF2F9"};
        color: ${portalColors.textSoft || "#8A95AB"};
        cursor: not-allowed;
    }
`;

export const PortalMetricGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(${p => p.columns || 3}, minmax(0, 1fr));
    gap: ${p => p.gap || "0.85rem"};

    @media (max-width: 980px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 620px) {
        grid-template-columns: 1fr;
    }
`;

export const PortalMetricCard = styled.div`
    min-width: 0;
    min-height: ${p => p.minHeight || "7rem"};
    grid-column: ${p => p.gridColumn || "auto"};
    grid-row: ${p => p.gridRow || "auto"};
    display: grid;
    gap: 0.6rem;
    align-content: space-between;
    padding: ${p => p.padding || "1rem"};
    background: ${p => p.background || portalColors.surfaceSoft};
    border: 1px solid ${p => p.border || portalColors.border};
    border-radius: ${p => p.radius || "12px"};
    box-sizing: border-box;
    animation: ${portalFadeUp} 0.24s ease both;
    animation-delay: ${p => p.$delay || "0s"};
    transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease, background 0.16s ease;

    &:hover {
        border-color: ${portalColors.borderStrong};
        box-shadow: 0 14px 28px rgba(29, 42, 89, 0.09);
        transform: translateY(-1px);
    }

    ${reducedMotion}
`;

export const PortalHighlightMetricCard = styled(PortalMetricCard)`
    background: ${p => p.background || portalColors.blueSoft};
    border: 1px solid ${p => p.border || portalColors.borderStrong};
`;

export const PortalMetricValue = styled.div`
    color: ${p => p.color || portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "2rem"};
    line-height: 1;
    font-weight: 800;
    letter-spacing: 0;
`;

export const PortalMetricLabel = styled.div`
    color: ${p => p.color || portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.82rem;
    line-height: 1.35;
    font-weight: 700;
`;

export const PortalEmptyState = styled.div`
    width: 100%;
    min-height: ${p => p.minHeight || "9rem"};
    display: grid;
    place-items: center;
    gap: 0.5rem;
    text-align: center;
    padding: 1.5rem;
    color: ${portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9rem;
    font-weight: 500;
    line-height: 1.5;
    background: ${portalColors.surfaceSoft};
    border: 1px dashed ${portalColors.borderStrong};
    border-radius: 12px;
    box-sizing: border-box;
    animation: ${portalFadeUp} 0.2s ease both;

    ${reducedMotion}
`;

export const PortalLoadingState = styled.div`
    width: 100%;
    min-height: ${p => p.minHeight || "14rem"};
    display: grid;
    place-items: center;
    background: ${portalColors.surface};
    border: 1px solid ${portalColors.border};
    border-radius: 16px;
    box-shadow: ${portalColors.shadow};
    box-sizing: border-box;
    animation: ${portalFadeUp} 0.18s ease both;

    ${reducedMotion}
`;

export const BusinessPageHeader = styled.header`
    width: 100%;
    max-width: ${p => p.maxWidth || "98.625rem"};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin: ${p => p.margin || "1rem 0"};
    padding: 0.9rem 1rem;
    box-sizing: border-box;
    background: ${portalColors.surface};
    border: 1px solid ${portalColors.border};
    border-radius: 16px;
    box-shadow: ${portalColors.shadow};
    animation: ${portalFadeUp} 0.24s ease both;
    transition: border-color 0.16s ease, box-shadow 0.16s ease;

    @media (max-width: 900px) {
        align-items: stretch;
        flex-direction: column;
        margin-top: 1.25rem;
    }

    ${reducedMotion}
`;

export const BusinessPageHeaderContent = styled.div`
    min-width: 0;
    flex: 1;
`;

export const BusinessPageHeading = styled.h1`
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 2rem;
    line-height: 1.15;
    font-weight: 800;
    margin: 0;
    max-width: 43.125rem;

    @media (max-width: 600px) {
        font-size: 1.75rem;
    }
`;

export const BusinessPageDescription = styled.p`
    color: ${p => p.color || portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.size || "0.875rem"};
    line-height: 1.5;
    font-weight: 500;
    margin: 0.5rem 0 0;
    max-width: ${p => p.maxWidth || "42rem"};
`;

export const BusinessHeaderControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.75rem;

    @media (max-width: 900px) {
        justify-content: flex-start;

        > * {
            max-width: 100%;
        }
    }
`;

export function BusinessPortalHeader({ title, description, children, maxWidth }) {
    return (
        <BusinessPageHeader maxWidth={maxWidth}>
            <BusinessPageHeaderContent>
                <BusinessPageHeading>{title}</BusinessPageHeading>
                {description && <BusinessPageDescription>{description}</BusinessPageDescription>}
            </BusinessPageHeaderContent>
            {children && <BusinessHeaderControls>{children}</BusinessHeaderControls>}
        </BusinessPageHeader>
    );
}

export const BusinessTablePanel = styled.section`
    width: 100%;
    max-width: ${p => p.maxWidth || "98.625rem"};
    background: ${portalColors.surface};
    border: 1px solid ${portalColors.border};
    border-radius: 16px;
    overflow: hidden;
    box-sizing: border-box;
    box-shadow: ${portalColors.shadow};
    animation: ${portalFadeUp} 0.22s ease both;

    ${reducedMotion}
`;

export const BusinessTableControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid ${portalColors.border};
    background: ${portalColors.surface};

    @media (max-width: 760px) {
        align-items: stretch;
        flex-direction: column;
    }
`;

export const BusinessTableScrollArea = styled.div`
    max-height: ${p => p.maxHeight || "42rem"};
    overflow: auto;
`;

export const BusinessModalPanel = styled.div`
    width: 100%;
    max-width: ${p => p.maxWidth || "61.25rem"};
    background: ${portalColors.surface};
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 16px;
    overflow: hidden;
    font-family: Manrope, Helvetica, sans-serif;
    box-shadow: 0 28px 70px rgba(0, 0, 0, 0.28);
    animation: ${portalFadeUp} 0.2s ease both;

    ${reducedMotion}
`;

export const BusinessModalHeader = styled.div`
    padding: 1.5rem 1.6rem 1.2rem;

    @media (max-width: 600px) {
        padding: 1.25rem;
    }
`;

export const BusinessModalHeading = styled.h2`
    color: ${portalColors.text};
    font-size: 1.75rem;
    line-height: 1.2;
    font-weight: 800;
    margin: 0;

    @media (max-width: 600px) {
        font-size: 1.75rem;
    }
`;

export const BusinessModalDivider = styled.div`
    height: 1px;
    background: ${portalColors.border};
`;

export const BusinessModalContent = styled.div`
    padding: 1.2rem 1.6rem 1.5rem;

    @media (max-width: 600px) {
        padding: 1.25rem;
    }
`;

export const BusinessModalActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 1rem 1.6rem 1.35rem;
    border-top: 1px solid ${portalColors.border};

    @media (max-width: 600px) {
        padding: 1rem 1.25rem;

        > * {
            flex: 1;
        }
    }
`;

export const LoadingButton = ({
    isLoading,
    loadingLabel = "Loading...",
    children,
    disabled,
    backgroundColor = portalColors.text,
    color = "var(--calendar-bg, #FFFFFF)",
    border = `1px solid ${portalColors.text}`,
    ...props
}) => (
    <Button
        disabled={disabled || isLoading}
        backgroundColor={backgroundColor}
        color={color}
        border={border}
        {...props}
    >
        {isLoading ? (
            <LottieLoadingThreeDots
                height="1.5rem"
                width="2.5rem"
                containerHeight="1.5rem"
                margin="0"
            />
        ) : children || loadingLabel}
    </Button>
);

const ToggleLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? 0.55 : 1};
`;

const ToggleInput = styled.input.attrs({ type: "checkbox" })`
    position: absolute;
    opacity: 0;
    pointer-events: none;
`;

const ToggleTrack = styled.span`
    position: relative;
    width: 2.75rem;
    height: 1.5rem;
    border-radius: 999px;
    background: ${p => p.checked ? portalColors.blue : "#D1DBE6"};
    transition: background 0.18s ease;

    &::after {
        content: "";
        position: absolute;
        top: 0.188rem;
        left: ${p => p.checked ? "1.438rem" : "0.188rem"};
        width: 1.125rem;
        height: 1.125rem;
        border-radius: 50%;
        background: var(--calendar-bg, #fff);
        box-shadow: 0 1px 3px #00000029;
        transition: left 0.18s ease;
    }
`;

export function ToggleSwitch({ checked, onChange, label, disabled, name }) {
    return (
        <ToggleLabel disabled={disabled}>
            <ToggleInput
                name={name}
                checked={checked}
                disabled={disabled}
                onChange={event => onChange(event.target.checked, event)}
            />
            <ToggleTrack checked={checked} />
            {label}
        </ToggleLabel>
    );
}

const SegmentedControl = styled.div`
    display: ${p => p.$equalWidth ? "grid" : "flex"};
    grid-template-columns: ${p => p.$equalWidth ? `repeat(${p.$optionCount}, minmax(0, 1fr))` : "none"};
    align-items: stretch;
    flex-wrap: wrap;
    gap: 0.75rem;
`;

const SegmentButton = styled.button`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    min-height: 42px;
    min-width: ${p => p.$equalWidth ? "0" : p.minWidth || "10rem"};
    width: ${p => p.$equalWidth ? "100%" : "auto"};
    padding: 0.75rem ${p => p.active ? "3rem" : "1.25rem"} 0.75rem 1.25rem;
    border: 1px solid ${p => p.active ? portalColors.blueDark : portalColors.border};
    border-radius: 8px;
    background: ${p => p.active ? portalColors.blueSoft : portalColors.surface};
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: ${p => p.active ? 700 : 500};
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    box-shadow: ${p => p.active ? `inset 0 0 0 1px ${portalColors.text}` : "none"};
    opacity: ${p => p.disabled ? 0.55 : 1};
`;

const CheckIcon = styled.img`
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
`;

export function SegmentedButtonGroup({ options, value, onChange, disabled, minWidth, equalWidth }) {
    return (
        <SegmentedControl role="radiogroup" $equalWidth={equalWidth} $optionCount={options.length}>
            {options.map(option => (
                <SegmentButton
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={option.value === value}
                    active={option.value === value}
                    disabled={disabled || option.disabled}
                    minWidth={minWidth}
                    $equalWidth={equalWidth}
                    onClick={() => !(disabled || option.disabled) && onChange(option.value)}
                >
                    {option.label}
                    {option.value === value && (
                        <CheckIcon src={checkIcon} width={20} height={20} alt="" />
                    )}
                </SegmentButton>
            ))}
        </SegmentedControl>
    );
}
